import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { TextInput } from '../';
import buildResults from './build-results';

const AddressSearch = ({
  id,
  name,
  label,
  validation,
  onChange,
  onSearch,
  onChooseAddress,
  getSuggestions,
  getFullAddress,
  suggestResults,
  fullAddress,
  disabled,
  ...props
}) => {
  const [addressSearchResults, setAddressSearchResults] = useState([]);

  useEffect(() => {
    if (!suggestResults?.suggestAddress) return;
    const results = buildResults(suggestResults.suggestAddress);
    setAddressSearchResults(results);
    onSearch(results);
  }, [suggestResults]); //  eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fullAddress?.recognizeAddress) return;
    onChooseAddress(fullAddress.recognizeAddress[0]);
  }, [fullAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAddressChange = async (address) => {
    onChange?.(address);
    if (!address || address.length < 5) return;
    await getSuggestionsQuery(address);
  };

  const getSuggestionsQuery = useMemo(
    () =>
      debounce(async (address) => {
        const suggestAddressInput = {
          address,
          maxSuggestions: 5,
          includePostalCode: true,
          returnAliases: true,
        };

        await getSuggestions({
          variables: { suggestAddressInput },
        });
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSelectAddress = async (address) => {
    const recognizeAddressInput = {
      address,
    };
    await getFullAddress({
      variables: { recognizeAddressInput },
    });
  };

  return (
    <TextInput
      label={label}
      name={name}
      fullWidth
      primary
      searchable
      searchResults={addressSearchResults}
      onChange={onAddressChange}
      onSelect={onSelectAddress}
      validation={validation}
      id={id}
      disabled={disabled}
      // eslint-disable-next-line react/prop-types
      className={props.className}
    />
  );
};

AddressSearch.propTypes = {
  /** An ID for the input. Useful to select the input during testing */
  id: PropTypes.string,
  /** The input name */
  name: PropTypes.string,
  /** The input label */
  label: PropTypes.any,
  /** An object representing the validation rules for the input */
  validation: PropTypes.object,
  /** A callback function called after something is typed or deleted from the input field.
   * NOTE: this does not trigger if onSelectAddress() changes the input */
  onChange: PropTypes.func,
  /** A callback function called after a search is completed */
  onSearch: PropTypes.func,
  /** A callback function called when an address search result is selected */
  onChooseAddress: PropTypes.func,
  /** Function to call for the address suggest query */
  getSuggestions: PropTypes.func,
  /** Function to call for the address recognize query */
  getFullAddress: PropTypes.func,
  /** Results for the address suggest query */
  suggestResults: PropTypes.object,
  /** Results for the address recognize query */
  fullAddress: PropTypes.object,
  /** Disable the associated text input */
  disabled: PropTypes.bool,
};

AddressSearch.defaultProps = {
  id: undefined,
  name: 'address',
  label: 'Address',
  validation: { required: true },
  onSearch: () => {},
  onChooseAddress: () => {},
  getSuggestions: undefined,
  getFullAddress: undefined,
  suggestResults: undefined,
  fullAddress: undefined,
  disabled: false,
};

export default AddressSearch;
